import type Geometry from 'ol/geom/Geometry';
import type { AppStore } from '../core/store';
import type { AppState } from '../core/types';
import { liveEffectiveFor } from '../domain/liveEffective';
import { planSuerteSplit, splittableSuerteFields, splitValueSuggestions, splitValuesError, type SplitBaseField, type SuerteSplitPlan } from '../domain/suerteSplit';
import type { DrawingController, PartialDrawingTool } from '../map/DrawingController';
import type { MapController } from '../map/MapController';
import { activeSplitField, suerteSplitAvailability, suerteSplitPatch } from '../presentation/suerteSplit';

const esc = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const ha = (m2: number): string => `${(m2 / 10_000).toLocaleString('es-NI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
const NEW_FIELD = '__new__';
type Step = 'setup' | 'drawing' | 'review';

/**
 * «Dividir estado de Suerte»: guided UX over the existing engine. Setup (dato + two states) → draw with the existing
 * DrawingController → review the planned split on the map (existing partial preview) → apply as ONE store update.
 * Cancel at any point leaves the project untouched: nothing is stored before «Aplicar división».
 */
export class SuerteSplitController {
  private suerteId: string | null = null;
  private tool: PartialDrawingTool = 'draw-polygon';
  private plan: SuerteSplitPlan | null = null;
  private drawn: Geometry | null = null;

  constructor(
    private readonly store: AppStore,
    private readonly drawing: DrawingController,
    private readonly map: MapController,
    private readonly status: (text: string) => void,
  ) {
    this.startButton.addEventListener('click', () => this.open());
    this.dialog.addEventListener('click', (event) => {
      const target = event.target as Element;
      const tool = target.closest<HTMLElement>('[data-split-tool]')?.dataset.splitTool as PartialDrawingTool | undefined;
      if (tool) { this.tool = tool; this.renderTool(); return; }
      const action = target.closest<HTMLElement>('[data-split-action]')?.dataset.splitAction;
      if (action === 'cancel') this.cancel();
      else if (action === 'draw') this.draw();
      else if (action === 'redraw') this.draw();
      else if (action === 'apply') this.apply();
    });
    this.fieldSelect.addEventListener('change', () => this.renderField());
    [this.drawnInput, this.restInput, this.newFieldInput].forEach((input) => input.addEventListener('input', () => this.validateSetup()));
    // Esc in the card behaves like Cancelar (the global shortcut must not clear the map selection meanwhile).
    this.dialog.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.cancel(); } });
  }

  private get dialog(): HTMLDialogElement { return document.querySelector<HTMLDialogElement>('#suerte-split-dialog')!; }
  private get startButton(): HTMLButtonElement { return document.querySelector<HTMLButtonElement>('#suerte-split-start')!; }
  private get fieldSelect(): HTMLSelectElement { return document.querySelector<HTMLSelectElement>('#split-field')!; }
  private get newFieldInput(): HTMLInputElement { return document.querySelector<HTMLInputElement>('#split-new-field-name')!; }
  private get drawnInput(): HTMLInputElement { return document.querySelector<HTMLInputElement>('#split-drawn-value')!; }
  private get restInput(): HTMLInputElement { return document.querySelector<HTMLInputElement>('#split-rest-value')!; }

  get isOpen(): boolean { return this.dialog.hasAttribute('open'); }

  /** Panel entry: enabled only for exactly one visible Suerte without Áreas parciales (V1). */
  render(state: AppState): void {
    const availability = suerteSplitAvailability(state);
    this.startButton.disabled = !availability.ok || this.isOpen;
    const hint = document.querySelector<HTMLElement>('#suerte-split-hint');
    if (hint) hint.textContent = availability.ok ? `${availability.label}: dibuja una parte y GeoStudio crea también el resto.` : availability.reason;
  }

  open(): void {
    const state = this.store.getState(); const availability = suerteSplitAvailability(state);
    if (!availability.ok) { this.store.update({ error: availability.reason }, { markDirty: false }); return; }
    this.suerteId = availability.suerteId; this.plan = null; this.drawn = null;
    document.querySelector('#split-heading')!.textContent = availability.label;
    const fields = splittableSuerteFields(state.operationalFields);
    const active = activeSplitField(state);
    this.fieldSelect.innerHTML = `${fields.map(({ descriptor }) => `<option value="${esc(descriptor.id)}">${esc(descriptor.name)}</option>`).join('')}<option value="${NEW_FIELD}">＋ Crear dato de Suerte…</option>`;
    this.fieldSelect.value = active ?? fields[0]?.descriptor.id ?? NEW_FIELD;
    this.newFieldInput.value = ''; this.drawnInput.value = ''; this.restInput.value = '';
    this.renderField(); this.renderTool();
    this.showStep('setup');
    if (!this.isOpen) { if (typeof this.dialog.show === 'function') this.dialog.show(); else this.dialog.setAttribute('open', ''); }
    if (window.matchMedia?.('(max-width: 760px)').matches) document.querySelector<HTMLElement>('[data-mobile-close]')?.click();
    this.fitSuerte();
    this.render(this.store.getState());
  }

  private renderField(): void {
    const isNew = this.fieldSelect.value === NEW_FIELD;
    (document.querySelector<HTMLElement>('#split-new-field')!).hidden = !isNew;
    const state = this.store.getState();
    const suggestions = splitValueSuggestions(state.operationalFields, isNew ? null : this.fieldSelect.value, isNew ? this.newFieldInput.value : '');
    document.querySelector('#split-values')!.innerHTML = suggestions.map((value) => `<option value="${esc(value)}"></option>`).join('');
    this.validateSetup();
  }

  private renderTool(): void {
    this.dialog.querySelectorAll<HTMLElement>('[data-split-tool]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.splitTool === this.tool)));
  }

  private base(): SplitBaseField | null {
    if (this.fieldSelect.value !== NEW_FIELD) return this.fieldSelect.value ? { fieldId: this.fieldSelect.value } : null;
    return this.newFieldInput.value.trim() ? { newName: this.newFieldInput.value.trim() } : null;
  }

  private validateSetup(): boolean {
    const error = !this.base() ? 'Escribe el nombre del dato de Suerte.' : splitValuesError(this.drawnInput.value, this.restInput.value);
    const warning = document.querySelector<HTMLElement>('#split-setup-warning')!;
    // Same state on both sides is shown as soon as it is typed; empty fields only block the button.
    warning.textContent = error && this.drawnInput.value.trim() && this.restInput.value.trim() ? error : '';
    this.dialog.querySelector<HTMLButtonElement>('[data-split-action="draw"]')!.disabled = Boolean(error);
    return !error;
  }

  private showStep(step: Step): void {
    this.dialog.querySelectorAll<HTMLElement>('[data-split-step]').forEach((element) => { element.hidden = element.dataset.splitStep !== step; });
    this.dialog.dataset.step = step;
  }

  private draw(): void {
    if (!this.suerteId || !this.validateSetup()) return;
    this.plan = null; this.drawn = null; this.map.setPartialAreaPreview(null);
    document.querySelector('#split-drawing-note')!.textContent = `Dibuja en el mapa la parte «${this.drawnInput.value.trim()}». Lo que quede fuera de la Suerte se descarta; el resto será «${this.restInput.value.trim()}». Esc para cancelar.`;
    this.showStep('drawing');
    this.fitSuerte();
    this.store.update({ activeTool: 'navigate', error: null }, { markDirty: false });
    const model = this.store.getState().model;
    this.drawing.capturePolygon(this.tool, (geometry) => this.drawnPart(geometry), {
      cancel: () => { if (this.isOpen && this.dialog.dataset.step === 'drawing') this.showStep('setup'); },
      live: { title: `Parte «${this.drawnInput.value.trim()}»`, split: true, ...(model ? { effective: liveEffectiveFor(model, { level: 'suerte', id: this.suerteId }) } : {}) },
    });
    this.status(`Dividir estado · dibuja la parte «${this.drawnInput.value.trim()}»`);
  }

  private drawnPart(geometry: Geometry): void {
    this.drawn = geometry;
    const warning = document.querySelector<HTMLElement>('#split-review-warning')!;
    try {
      this.plan = this.makePlan(geometry);
    } catch (error) {
      this.plan = null; this.map.setPartialAreaPreview(null);
      this.showStep('setup');
      document.querySelector<HTMLElement>('#split-setup-warning')!.textContent = error instanceof Error ? error.message : 'No fue posible preparar la división.';
      return;
    }
    const plan = this.plan;
    this.map.setPartialAreaPreview(plan.drawnPreview.geometry, plan.restPreview.geometry);
    const parentM2 = plan.drawnPreview.parentGeometryAreaM2;
    const coverage = parentM2 > 0 ? (plan.drawnPreview.effectiveAreaM2 + plan.restPreview.effectiveAreaM2) / parentM2 * 100 : 0;
    document.querySelector('#split-review')!.innerHTML = `<div class="split-review-grid">
      <div class="split-part split-part-drawn"><span>Parte dibujada</span><b>${esc(plan.drawnValue)}</b><em>${ha(plan.drawnPreview.effectiveAreaM2)}</em></div>
      <div class="split-part split-part-rest"><span>Resto de la Suerte</span><b>${esc(plan.restValue)}</b><em>${ha(plan.restPreview.effectiveAreaM2)}</em></div>
    </div>
    <dl class="split-facts"><div><dt>Dato</dt><dd>${esc(plan.baseFieldName)}</dd></div><div><dt>Cobertura total</dt><dd>${coverage.toLocaleString('es-NI', { maximumFractionDigits: 1 })} %</dd></div><div><dt>Partes resultantes</dt><dd>${plan.drawnPreview.resultPartCount} / ${plan.restPreview.resultPartCount}</dd></div>${plan.drawnPreview.discardedAreaM2 > 1 ? `<div><dt>Fuera de la Suerte (descartado)</dt><dd>${ha(plan.drawnPreview.discardedAreaM2)}</dd></div>` : ''}</dl>
    <p class="pane-note">Se crearán dos Áreas parciales («${esc(plan.drawnValue)}» y «${esc(plan.restValue)}») y el mapa mostrará la Suerte dividida en la misma leyenda. El Maestro no cambia.</p>`;
    warning.textContent = '';
    this.showStep('review');
    this.fitSuerte(); // the review card is taller: keep the whole Suerte above it (phones)
    this.status('Dividir estado · revisa las áreas y aplica');
  }

  private makePlan(geometry: Geometry): SuerteSplitPlan {
    const state = this.store.getState(); const base = this.base();
    if (!state.model || !this.suerteId || !base) throw new Error('Vuelve a abrir «Dividir estado de Suerte».');
    return planSuerteSplit({ model: state.model, subareas: state.subareas, fields: state.operationalFields, suerteId: this.suerteId, base, drawnValue: this.drawnInput.value, restValue: this.restInput.value, drawn: geometry });
  }

  /** One store update with everything (or nothing): subareas, field and values, classification, selection. */
  private apply(): void {
    const warning = document.querySelector<HTMLElement>('#split-review-warning')!;
    if (!this.plan) return;
    try {
      let plan = this.plan; const state = this.store.getState();
      // The project changed since the preview (e.g. an autosave restore): re-plan from the same drawing, still atomically.
      if (state.model !== plan.basis.model || state.subareas !== plan.basis.subareas || state.operationalFields !== plan.basis.fields) plan = this.makePlan(this.drawn!);
      const patch = suerteSplitPatch(state, plan);
      this.store.update(patch);
      this.status(`Suerte dividida · ${plan.drawnValue} ${ha(plan.drawnPreview.effectiveAreaM2)} · ${plan.restValue} ${ha(plan.restPreview.effectiveAreaM2)}`);
      this.close();
    } catch (error) {
      warning.textContent = error instanceof Error ? error.message : 'No fue posible aplicar la división.';
    }
  }

  cancel(): void {
    if (this.dialog.dataset.step === 'drawing') this.drawing.cancelSketch();
    if (this.isOpen) this.status('División cancelada · no se guardó nada');
    this.close();
  }

  private close(): void {
    this.plan = null; this.drawn = null; this.suerteId = null;
    this.map.setPartialAreaPreview(null);
    if (this.isOpen) { if (typeof this.dialog.close === 'function') this.dialog.close(); this.dialog.removeAttribute('open'); }
    delete this.dialog.dataset.step;
    this.render(this.store.getState());
  }

  /** Keeps the Suerte inside the map area left free by the card (bottom sheet on phones). */
  private fitSuerte(): void {
    const state = this.store.getState(); const suerte = this.suerteId ? state.model?.suertes.get(this.suerteId) : undefined;
    if (!suerte) return;
    const extent = suerte.fragmentIds.reduce<number[]>((acc, id) => {
      const e = state.model!.fragmentsById.get(id)?.feature.getGeometry()?.getExtent(); if (!e) return acc;
      return [Math.min(acc[0]!, e[0]!), Math.min(acc[1]!, e[1]!), Math.max(acc[2]!, e[2]!), Math.max(acc[3]!, e[3]!)];
    }, [Infinity, Infinity, -Infinity, -Infinity]);
    if (!extent.every(Number.isFinite)) return;
    const dialog = this.dialog; const target = this.map.map.getTargetElement();
    requestAnimationFrame(() => {
      if (!dialog?.isConnected || !target?.isConnected) return;
      const mapRect = target.getBoundingClientRect(); const card = dialog.getBoundingClientRect();
      const bottom = dialog.hasAttribute('open') && card.height ? Math.max(0, mapRect.bottom - card.top) : 0;
      this.map.fitInVisibleArea(extent, { bottom });
    });
  }
}
